
import React, { useState, useEffect } from 'react';
import { ExperienceItem } from '@/hooks/profile';
import { cn } from '@/lib/utils';
import { calculateDuration, getShortDuration } from '@/utils/durationCalculator';
import { Dot } from 'lucide-react';

interface TimelineExperienceItemProps {
  item: ExperienceItem;
  index: number;
  isLast: boolean;
}

const TimelineExperienceItem = ({
  item,
  index,
  isLast
}: TimelineExperienceItemProps) => {
  // State to hold the formatted duration, will be updated periodically for current positions
  const [duration, setDuration] = useState('');
  const [shortDuration, setShortDuration] = useState('');

  // Update duration for current positions every day to keep it accurate
  useEffect(() => {
    // Calculate initial duration
    const calculateAndSetDuration = () => {
      const calculatedDuration = calculateDuration(item.start_date, item.end_date, item.current || false);
      setDuration(calculatedDuration);
      setShortDuration(calculatedDuration); // Use full duration with months instead of shortened version
    };
    calculateAndSetDuration();

    // If this is a current position, set up an interval to update the duration
    let intervalId: number | null = null;
    if (item.current) {
      // Update once per day (86400000 ms)
      intervalId = window.setInterval(calculateAndSetDuration, 86400000);
    }

    // Clean up interval on component unmount
    return () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
      }
    };
  }, [item.start_date, item.end_date, item.current]);

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '';
    try {
      // Try to parse as YYYY-MM
      const date = new Date(dateString + '-01'); // Add day for proper parsing
      return date.toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric'
      });
    } catch {
      // Return original if parsing fails
      return dateString;
    }
  };

  // Format the duration text with reduced opacity for months only when they appear after years
  const formatDurationText = (text: string) => {
    if (!text.includes('month')) return text;
    
    // Split by comma to separate years and months
    const parts = text.split(',');
    if (parts.length === 1) {
      // Only months, no opacity reduction
      return <>{parts[0]}</>;
    } else {
      // Has years and months, reduce opacity only for the months part
      return (
        <>
          {parts[0]}
          <span className="opacity-60">,{parts[1]}</span>
        </>
      );
    }
  };

  return <div className="relative pl-16 pb-8 mb-6">
      {/* Vertical timeline line that runs continuously on the left side */}
      <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-gray-200 mx-[34px]">
        {isLast && <div className="absolute bottom-0 h-6 w-0.5 bg-white"></div>}
      </div>
      
      {/* Container for the entire content - card + dot */}
      <div className="relative">
        {/* Timeline dot with duration text positioned higher to be visible above the card */}
        <div className="absolute -left-16 -top-10 flex items-center px-px mx-[34px] py-0">
          <div className="relative">
            <Dot className="h-18 w-18 text-accent scale-[3]" />
          </div>
          <span className="text-sm font-medium text-gray-700 py-0 my-0 mx-[5px]">
            {formatDurationText(shortDuration)}
          </span>
        </div>
        
        {/* Content Card */}
        <div className={cn("bg-white rounded-lg p-5 shadow-sm transition-all duration-300 max-w-xl", "hover:shadow-md animate-fade-in border border-gray-50")}>
          <div>
            <h3 className="text-xl font-semibold mb-1">{item.position}</h3>
            <p className="text-gray-700 mb-1">
              {item.company}
              {item.location && <span className="text-gray-500"> • {item.location}</span>}
            </p>
            <p className="text-gray-500 text-sm">
              {formatDate(item.start_date)} – {item.current ? 'Present' : formatDate(item.end_date)}
            </p>
          </div>
          
          {item.description && <p className="mt-3 text-gray-600">{item.description}</p>}
        </div>
      </div>
    </div>;
};

export default TimelineExperienceItem;
